/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { createPage, updatePageInfo } from '../../../api/notes'
import { useApplicationState } from '../../../hooks/common/use-application-state'
import { setNoteMetadataFromServer } from '../../../redux/note-details/methods'
import { renameNoteTreeEntry, updateNoteTreeEntry } from '../../../redux/note-tree/methods'
import { cypressId } from '../../../utils/cypress-attribute'
import { buildNoteUrl } from '../../../utils/note-url'
import { useUiNotifications } from '../../notifications/ui-notification-boundary'
import { CommonModal } from '../modals/common-modal'
import { NoteSettingsProvider } from '../note-settings/note-settings-context'
import type { PageSettingsModalContentProps } from './page-settings-modal-content'
import { PageSettingsModalCreateContent, PageSettingsModalEditContent } from './page-settings-modal-content'
import { buildNoteSlug, NOTE_PATH_MAX_DEPTH, splitNotePath } from '@hedgedoc/commons'
import { useRouter } from 'next/navigation'
import type { ChangeEvent, FormEvent } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileEarmarkPlus as IconFileEarmarkPlus, Gear as IconGear } from 'react-bootstrap-icons'

export interface PageSettings {
  title: string
  path: string
  badge: string
}

export interface PageSettingsModalProps {
  show: boolean
  onHide: () => void
  /** Whether a new page is created or the settings of the current note are edited */
  mode: 'create' | 'edit'
  /** The values the form is filled with when the modal is opened */
  initialSettings: PageSettings
  /** The alias of the note to edit, defaults to the currently opened note */
  noteAlias?: string
}

/**
 * A modal to create a new page with a badge, title and path, or to view and change the badge, path, info and permissions of a note.
 * The url of the page is generated from its path and title.
 * Changes of the permissions are applied immediately, the other settings when the form is submitted.
 *
 * @param show Whether the modal is visible
 * @param onHide Is called when the modal should be closed
 * @param mode Whether a page is created or the current note is edited
 * @param initialSettings The values the form is filled with
 * @param noteAlias The alias of the note to edit, defaults to the currently opened note
 */
export const PageSettingsModal: React.FC<PageSettingsModalProps> = ({
  show,
  onHide,
  mode,
  initialSettings,
  noteAlias
}) => {
  const [settings, setSettings] = useState<PageSettings>(initialSettings)
  const [submitting, setSubmitting] = useState(false)
  const router = useRouter()
  const { showErrorNotificationBuilder } = useUiNotifications()
  const openNoteAlias = useApplicationState((state) => state.noteDetails?.primaryAlias)
  const targetAlias = noteAlias ?? openNoteAlias

  // Only reset the form when the modal is opened, not when the initial settings change while it is open
  const initialSettingsRef = useRef(initialSettings)
  initialSettingsRef.current = initialSettings
  useEffect(() => {
    if (show) {
      setSettings(initialSettingsRef.current)
      setSubmitting(false)
    }
  }, [show])

  const onTitleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const title = event.currentTarget.value
    setSettings((old) => ({ ...old, title }))
  }, [])
  const onPathChange = useCallback((path: string) => {
    setSettings((old) => ({ ...old, path }))
  }, [])
  const onBadgeChange = useCallback((badge: string) => {
    setSettings((old) => ({ ...old, badge }))
  }, [])

  const pathTooDeep = splitNotePath(settings.path).length > NOTE_PATH_MAX_DEPTH
  const slugPreview = useMemo(() => buildNoteUrl(buildNoteSlug(settings.path, settings.title)), [settings])

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (pathTooDeep || submitting) {
        return
      }
      setSubmitting(true)
      if (mode === 'create') {
        createPage(settings)
          .then((note) => {
            const { primaryAlias, title, path, badge } = note.metadata
            updateNoteTreeEntry({ primaryAlias, title, path, badge })
            onHide()
            router.push(buildNoteUrl(primaryAlias))
          })
          .catch(showErrorNotificationBuilder('pageSettings.createTitle', {}, true))
          .finally(() => setSubmitting(false))
      } else if (targetAlias) {
        updatePageInfo(targetAlias, { path: settings.path, badge: settings.badge })
          .then((metadata) => {
            const { primaryAlias, title, path, badge } = metadata
            if (primaryAlias !== targetAlias) {
              renameNoteTreeEntry({ from: targetAlias, to: primaryAlias })
            }
            updateNoteTreeEntry({ primaryAlias, title, path, badge })
            if (targetAlias === openNoteAlias) {
              setNoteMetadataFromServer(metadata)
            }
            onHide()
          })
          .catch(showErrorNotificationBuilder('pageSettings.editTitle', {}, true))
          .finally(() => setSubmitting(false))
      }
    },
    [mode, onHide, openNoteAlias, pathTooDeep, router, settings, showErrorNotificationBuilder, submitting, targetAlias]
  )

  // Existing notes show their info and permissions, which are loaded before the content is shown
  const editAlias = mode === 'edit' ? targetAlias : undefined
  const contentProps: PageSettingsModalContentProps = {
    mode,
    settings,
    submitting,
    pathTooDeep,
    slugPreview,
    onTitleChange,
    onPathChange,
    onBadgeChange,
    onSubmit
  }

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      titleI18nKey={mode === 'create' ? 'pageSettings.createTitle' : 'pageSettings.editTitle'}
      titleIcon={mode === 'create' ? IconFileEarmarkPlus : IconGear}
      showCloseButton={true}
      modalSize={editAlias !== undefined ? 'xl' : undefined}
      {...cypressId('page-settings-modal')}>
      {editAlias !== undefined ? (
        <NoteSettingsProvider noteAlias={editAlias}>
          <PageSettingsModalEditContent {...contentProps} />
        </NoteSettingsProvider>
      ) : (
        <PageSettingsModalCreateContent {...contentProps} />
      )}
    </CommonModal>
  )
}
